import React from 'react';
import { compactMoney, fullMoney } from '../format';

const STRUCTURE_LABELS = {
  cash: 'All cash',
  seller_financing: 'Seller financing',
  earnout: 'Earnout',
  blended: 'Blended',
};

const SEGMENTS = [
  { key: 'closing', label: 'At closing', bar: 'bg-succeedo-orange' },
  { key: 'note', label: 'Seller note', bar: 'bg-[hsl(var(--chart-1))]' },
  { key: 'earnout', label: 'Earnout', bar: 'bg-[hsl(var(--chart-3))]' },
  { key: 'consulting', label: 'Consulting', bar: 'bg-[hsl(var(--chart-2))]' },
];

// Deal Structure (PRD §6.3, Phase 2): how the price arrives. Key figure is
// what the owner keeps after tax; a split bar shows closing cash against
// money paid later (and at whose risk), then the year-by-year timeline.
export default function DealStructureBody({ artifact, data }) {
  const amounts = {
    closing: data.cash_at_closing || 0,
    note: data.seller_note?.principal || 0,
    earnout: data.earnout?.total_amount || 0,
    consulting: data.consulting?.total_income || 0,
  };
  const total = Math.max(Object.values(amounts).reduce((a, b) => a + b, 0), 1);
  const parts = SEGMENTS.filter((s) => amounts[s.key] > 0);
  const timeline = (data.timeline || []).filter((row) => row.amount > 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <span className="text-[28px] font-light tracking-[-0.02em] tabular-nums">
          {compactMoney(data.after_tax_proceeds)}
          <span className="ml-1.5 text-[15px] text-muted-foreground tracking-normal">after tax</span>
        </span>
        <span className="text-[14px] text-muted-foreground tabular-nums">
          {STRUCTURE_LABELS[data.structure_type] || 'Deal'} · {fullMoney(data.price)} price
          {data.active === false ? ' · scenario' : ''}
        </span>
      </div>
      {artifact.summary && <p className="m-0 text-[15px] leading-[1.6] text-foreground/[0.88]">{artifact.summary}</p>}

      <div className="flex flex-col gap-2.5">
        <div
          role="img"
          aria-label={parts.map((s) => `${s.label} ${fullMoney(amounts[s.key])}`).join(', ')}
          className="flex h-2.5 rounded-full overflow-hidden gap-0.5 bg-border"
        >
          {parts.map((s) => (
            <div key={s.key} className={s.bar} style={{ width: `${(amounts[s.key] / total) * 100}%` }} />
          ))}
        </div>
        <ul className="m-0 p-0 list-none flex flex-wrap gap-x-5 gap-y-1.5">
          {parts.map((s) => (
            <li key={s.key} className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground tabular-nums">
              <span className={`w-2 h-2 rounded-full ${s.bar}`} />
              {s.label} <span className="text-foreground">{compactMoney(amounts[s.key])}</span>
            </li>
          ))}
        </ul>
        {data.deferred > 0 && (
          <span className="text-[13px] text-amber-ink" data-testid="deal-deferred">
            {data.deferred_pct}% of the price arrives after closing
          </span>
        )}
      </div>

      {timeline.length > 1 && (
        <table className="w-full border-collapse text-[13px] tabular-nums">
          <caption className="sr-only">When the money arrives</caption>
          <tbody>
            {timeline.map((row) => (
              <tr key={row.period} className="border-t border-border/60">
                <th scope="row" className="text-left font-normal py-2 px-0 text-muted-foreground">{row.period}</th>
                <td className="text-right py-2 px-0">{fullMoney(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
